/*
  The page's side of the service worker (`scripts/service-worker.js`): registering it, finding out
  that there is a new version, taking the new version, and removing it all again.

  REGISTERED ONLY ONCE A SESSION EXISTS. A visitor who has not signed in has asked for the landing
  page and nothing more, and a worker is storage on their device (`client/docs/privacy-storage.md`
  says what it holds and why that needs no banner); `main.ts` calls `registerWhenSignedIn` with the
  answer once the session is restored, never before. A build that is not production has no `/sw.js` to register — the dev
  server would answer it with index.html — so it does nothing there either.

  A NEW VERSION IS OFFERED, NEVER FORCED. A build is a new worker; the browser installs it beside the
  old one and leaves it waiting. `updateAvailable` is true once there is one waiting while an old one
  controls this page, and `applyUpdate` is the person's answer: it asks the waiting worker to take over,
  and reloads when it has — not before, and only for the window that asked, since the worker claims
  every window and a reload for all of them would throw away what the others hold.
*/
import { readonly, ref } from "vue";

/** Where the worker is served from, which is also what it controls: the whole app. */
const SCRIPT_URL = "/sw.js";

/** The start of every cache the worker makes, which is how a new build finds the old ones. */
const CACHE_PREFIX = "notitia-";

/** How often an open window asks the server whether there is a new version. */
const UPDATE_CHECK_MS = 30 * 60 * 1000;

const available = ref(false);

/** A new version has been downloaded and is waiting for the person to say Reload. */
export const updateAvailable = readonly(available);

let registration: ServiceWorkerRegistration | null = null;
let reloadWhenTaken = false;
let checking: Promise<void> | null = null;
let listening = false;

/**
 * Registers the worker, if this browser has them and this is a production build, and starts
 * looking for new versions. Safe to call twice. A registration that fails is not an error the person
 * can do anything about — the app works without it — so it is left for the next load.
 *
 * `enabled` is for tests; the default is the build's own.
 */
export async function registerServiceWorker(options: { enabled?: boolean } = {}): Promise<void> {
	const { enabled = import.meta.env.PROD } = options;
	if (!enabled || registration || !("serviceWorker" in navigator)) return;
	try {
		registration = await navigator.serviceWorker.register(SCRIPT_URL, {
			scope: "/",
			// The browser's HTTP cache is not asked about the worker or what it imports, so a new
			// build is found the first time it is looked for.
			updateViaCache: "none",
		});
	} catch {
		return;
	}
	noticeUpdates(registration);
	checkOften();
}

/**
 * What `main.ts` calls, with the answer to "is there a session": a visitor who has none gets
 * nothing registered. The gate is here, not at the call, so that a test can hold it.
 */
export async function registerWhenSignedIn(
	signedIn: boolean,
	options: { enabled?: boolean } = {},
): Promise<void> {
	if (signedIn) await registerServiceWorker(options);
}

/** True once a worker is waiting while another one controls this page. */
function noticeUpdates(current: ServiceWorkerRegistration): void {
	const consider = () => {
		if (current.waiting && navigator.serviceWorker.controller) available.value = true;
	};
	consider();
	current.addEventListener("updatefound", () => {
		const incoming = current.installing;
		incoming?.addEventListener("statechange", () => {
			if (incoming.state === "installed") consider();
		});
	});
	navigator.serviceWorker.addEventListener("controllerchange", () => {
		// The first worker claims the page it was installed from, which is also a change of
		// controller: only the one a person asked for is a reload.
		if (!reloadWhenTaken) return;
		reloadWhenTaken = false;
		window.location.reload();
	});
}

/** Asks the server whether there is a new version; one request at a time, and never an error. */
export function checkForUpdate(): Promise<void> {
	if (!registration) return Promise.resolve();
	checking ??= registration
		.update()
		.then(
			() => undefined,
			// Offline, or the server is restarting: the next check tries again.
			() => undefined,
		)
		.finally(() => {
			checking = null;
		});
	return checking;
}

/** On coming back to the window, and on a timer for one that is simply left open. */
function checkOften(): void {
	if (listening) return;
	listening = true;
	window.addEventListener("focus", () => void checkForUpdate());
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") void checkForUpdate();
	});
	window.setInterval(() => void checkForUpdate(), UPDATE_CHECK_MS);
}

/**
 * The person's answer to the prompt: take the waiting version, and reload into it. With nothing
 * waiting any more — another window took it — the page is simply reloaded, which is what it needs.
 */
export function applyUpdate(): void {
	const waiting = registration?.waiting;
	if (!waiting) {
		window.location.reload();
		return;
	}
	reloadWhenTaken = true;
	waiting.postMessage({ type: "SKIP_WAITING" });
}

/**
 * Takes the worker and everything it keeps off this device: every registration of this origin,
 * and every cache the app made. What the Local data panel's button does. The worker is registered
 * again the next time the app is opened signed in — there is nothing to switch off, since there is
 * nothing in it that belongs to a person.
 */
export async function clearServiceWorker(): Promise<void> {
	if ("serviceWorker" in navigator) {
		const registrations = await navigator.serviceWorker.getRegistrations();
		await Promise.all(registrations.map((entry) => entry.unregister()));
	}
	if ("caches" in globalThis) {
		const keys = await caches.keys();
		await Promise.all(
			keys.filter((key) => key.startsWith(CACHE_PREFIX)).map((key) => caches.delete(key)),
		);
	}
	registration = null;
	available.value = false;
}
